import { lazy, Suspense, useState } from "react";
import { Menu, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link, useLocation } from "react-router-dom";
import vibraLogo from "@/assets/vibra-logo.webp";

// Lazy: el megamenú importa 7 iconos PNG + JSX pesado. Solo se carga al hover.
const ServicesMegaMenu = lazy(() => import("./ServicesMegaMenu"));

const agenciaLinks = [
  { label: "Equipo", href: "/sobre-nosotros" },
  { label: "Casos", href: "/casos-de-exito" },
  { label: "Precios", href: "/precios" },
  { label: "Contacto", href: "/contacto" },
];

const Header = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const [articulosOpen, setArticulosOpen] = useState(false);
  const location = useLocation();
  const isHome = location.pathname === "/";


  const handleAnchorClick = (e: React.MouseEvent<HTMLAnchorElement>, href: string) => {
    if (isHome && href.startsWith("#")) {
      e.preventDefault();
      if (href === "#top") {
        window.scrollTo({ top: 0, behavior: "smooth" });
      } else {
        const target = document.querySelector(href);
        if (target) {
          target.scrollIntoView({ behavior: "smooth" });
        }
      }
      setIsOpen(false);
    }
  };

  const handleScrollToPricing = () => {
    if (isHome) {
      const target = document.querySelector("#pricing");
      if (target) {
        target.scrollIntoView({ behavior: "smooth" });
      }
    } else {
      window.location.href = "/#pricing";
    }
  };

  return (
    <header className={`fixed top-0 left-0 right-0 z-40 border-b border-border/50 transition-colors duration-300 ${isOpen ? "bg-background" : "bg-background/95"}`}>
      <div className="container mx-auto px-6">
        <div className="flex items-center justify-between h-16">
          <Link to="/" className="flex items-center">
            <img src={vibraLogo} alt="Vibra Marketing" width="140" height="36" className="h-9 w-auto" />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-8">
            <a
              href={isHome ? "#top" : "/"}
              onClick={(e) => handleAnchorClick(e, "#top")}
              className="text-sm text-muted-foreground hover:text-foreground transition-colors duration-200"
            >
              Home
            </a>
            {/* Servicios megamenu trigger */}
            <div
              className="relative"
              onMouseEnter={() => setServicesOpen(true)}
              onMouseLeave={() => setServicesOpen(false)}
            >
              <a
                href={isHome ? "#services" : "/#services"}
                onClick={(e) => handleAnchorClick(e, "#services")}
                className="text-sm text-muted-foreground hover:text-foreground transition-colors duration-200 py-5"
              >
                Servicios
              </a>
            </div>
            {agenciaLinks.map((link) => (
              <Link
                key={link.label}
                to={link.href}
                className="text-sm text-muted-foreground hover:text-foreground transition-colors duration-200"
              >
                {link.label}
              </Link>
            ))}
            {/* Artículos dropdown */}
            <div
              className="relative"
              onMouseEnter={() => setArticulosOpen(true)}
              onMouseLeave={() => setArticulosOpen(false)}
            >
              <button
                type="button"
                className="text-sm text-muted-foreground hover:text-foreground transition-colors duration-200 py-5"
              >
                Artículos
              </button>
              <div
                className={`absolute left-1/2 -translate-x-1/2 top-full min-w-[200px] bg-background border border-border rounded-xl shadow-xl p-2 transition-all duration-200 origin-top ${
                  articulosOpen
                    ? "opacity-100 visible translate-y-0"
                    : "opacity-0 invisible -translate-y-2 pointer-events-none"
                }`}
              >
                <Link
                  to="/guias"
                  onClick={() => setArticulosOpen(false)}
                  className="block px-4 py-2.5 rounded-lg text-sm text-foreground hover:bg-card hover:text-primary transition-colors"
                >
                  <div className="font-medium">Guías</div>
                  <div className="text-xs text-muted-foreground">Recursos pilar por servicio</div>
                </Link>
                <Link
                  to="/noticias"
                  onClick={() => setArticulosOpen(false)}
                  className="block px-4 py-2.5 rounded-lg text-sm text-foreground hover:bg-card hover:text-primary transition-colors"
                >
                  <div className="font-medium">Noticias</div>
                  <div className="text-xs text-muted-foreground">Actualidad con impacto</div>
                </Link>
              </div>
            </div>
            <Link
              to="/faq"
              className="text-sm text-muted-foreground hover:text-foreground transition-colors duration-200"
            >
              FAQ
            </Link>
          </nav>


          <div className="hidden md:flex items-center gap-3">
            <a
              href="https://clientes.vibramarketing.es/"
              target="_blank"
              rel="noopener noreferrer"
              className="btn-pill bg-foreground text-background hover:bg-foreground/90"
            >
              Acceso clientes
            </a>
            <a
              href={isHome ? "#pricing" : "/#pricing"}
              onClick={(e) => {
                if (isHome) {
                  e.preventDefault();
                  handleScrollToPricing();
                }
              }}
              className="btn-pill bg-primary text-primary-foreground hover:shadow-glow"
            >
              Agendar reunión
            </a>
          </div>

          {/* Mobile Menu Button */}
          <div className="md:hidden flex items-center gap-2">
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="p-2 text-muted-foreground hover:text-foreground transition-colors"
              aria-label={isOpen ? "Cerrar menú" : "Abrir menú"}
            >
              {isOpen ? <X size={20} /> : <Menu size={20} />}
            </button>
          </div>
        </div>

        {/* Mobile Navigation — panel lateral desde la derecha */}
        {isOpen && (
          <>
            <div
              className="fixed inset-0 z-30 bg-foreground/20 backdrop-blur-sm md:hidden"
              onClick={() => setIsOpen(false)}
              aria-hidden="true"
            />
            <div className="fixed top-0 right-0 bottom-0 z-50 w-[80vw] max-w-[320px] bg-background shadow-2xl border-l border-primary/20 md:hidden animate-slide-in-right rounded-l-2xl">
              <button
                type="button"
                onClick={() => setIsOpen(false)}
                aria-label="Cerrar menú"
                className="absolute top-4 right-4 p-2 text-muted-foreground hover:text-foreground transition-colors z-50"
              >
                <X size={24} />
              </button>
              <nav className="flex flex-col h-full pt-20 pb-6 px-6">
                <div className="flex flex-col overflow-y-auto flex-1">
                  <Link
                    to="/"
                    className="text-lg font-medium text-foreground py-3 hover:text-primary transition-colors"
                    onClick={() => setIsOpen(false)}
                  >
                    Home
                  </Link>
                  <div className="h-px bg-gradient-to-r from-primary/60 via-primary to-primary/60" />
                  <a
                    href={isHome ? "#services" : "/#services"}
                    onClick={(e) => {
                      handleAnchorClick(e, "#services");
                      setIsOpen(false);
                    }}
                    className="text-lg font-medium text-foreground py-3 hover:text-primary transition-colors"
                  >
                    Servicios
                  </a>
                  <div className="h-px bg-gradient-to-r from-primary/60 via-primary to-primary/60" />
                  {agenciaLinks.map((link) => (
                    <Link
                      key={link.label}
                      to={link.href}
                      className="text-lg font-medium text-foreground py-3 hover:text-primary transition-colors"
                      onClick={() => setIsOpen(false)}
                    >
                      {link.label}
                    </Link>
                  ))}
                  <div className="h-px bg-gradient-to-r from-primary/60 via-primary to-primary/60" />
                  <Link
                    to="/faq"
                    className="text-lg font-medium text-foreground py-3 hover:text-primary transition-colors"
                    onClick={() => setIsOpen(false)}
                  >
                    FAQ
                  </Link>
                  <div className="h-px bg-gradient-to-r from-primary/60 via-primary to-primary/60" />
                </div>

                <div className="sticky bottom-0 bg-background pt-4 -mx-6 px-6 border-t border-border/50 flex flex-col gap-3 shrink-0">
                  <Button variant="outline" size="lg" className="w-full justify-center" asChild>
                    <a href="https://clientes.vibramarketing.es/" target="_blank" rel="noopener noreferrer">
                      Acceso clientes
                    </a>
                  </Button>
                  <Button size="lg" className="w-full" onClick={() => { setIsOpen(false); handleScrollToPricing(); }}>
                    Agendar reunión
                  </Button>
                </div>
              </nav>
            </div>
          </>
        )}
      </div>

      {/* Full-width Servicios megamenu — lazy: solo se carga al primer hover */}
      <div
        onMouseEnter={() => setServicesOpen(true)}
        onMouseLeave={() => setServicesOpen(false)}
        className={`hidden md:block absolute left-0 right-0 top-full bg-background border-b border-border shadow-xl transition-all duration-200 origin-top ${
          servicesOpen ? "opacity-100 visible translate-y-0" : "opacity-0 invisible -translate-y-2 pointer-events-none"
        }`}
      >
        {servicesOpen && (
          <Suspense fallback={<div className="container mx-auto px-6 py-8 h-[400px]" />}>
            <ServicesMegaMenu onNavigate={() => setServicesOpen(false)} />
          </Suspense>
        )}
      </div>
    </header>
  );
};

export default Header;
